import { useState } from 'react';
import { Copy, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { TopNav } from '@/components/TopNav';
import { BottomNavigation } from '@/components/BottomNavigation';
import { HistoryDrawer } from '@/components/HistoryDrawer';
import { FaqDrawer } from '@/components/FaqDrawer';
import { SettingsSheet } from '@/components/SettingsSheet';
import { PrizeTiersDrawer } from '@/components/PrizeTiersDrawer';
import { ReferralErrorModal } from '@/components/ReferralErrorModal';
import { ReferralCampaignProgress } from '@/components/ReferralCampaignProgress';
import { useReferral } from '@/hooks/useReferral';
import { useAuth } from '@/hooks/useAuth';
import { sanitizeInviteCodeInput, getInviteCodeError } from '@/utils/referralValidation';

export default function ReferralPage() {
  const navigate = useNavigate();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prizeTiersOpen, setPrizeTiersOpen] = useState(false);
  const [inviteCodeInput, setInviteCodeInput] = useState('');
  const [showBindConfirmation, setShowBindConfirmation] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { isConnected } = useWalletAddress();
  const { isAuthenticated, authenticate, isAuthenticating } = useAuth();
  const { 
    inviteInfo, 
    isLoadingInfo, 
    dashboard, 
    isLoadingDashboard, 
    bindCode, 
    isBindingCode,
    bindError,
    clearBindError
  } = useReferral();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleInviteCodeChange = (value: string) => {
    // Sanitize input to only allow valid characters
    const sanitized = sanitizeInviteCodeInput(value);
    setInviteCodeInput(sanitized);

    // Clear validation error when user types
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleBindCodeClick = () => {
    const code = inviteCodeInput.trim().toUpperCase();

    // Validate the code format
    const error = getInviteCodeError(code);
    if (error) {
      setValidationError(error);
      toast.error(error);
      return;
    }

    // Show confirmation dialog
    setShowBindConfirmation(true);
  };

  const confirmBindCode = () => {
    const code = inviteCodeInput.trim().toUpperCase();

    // Bind the code (validation is done server-side)
    bindCode(code);

    // Close confirmation and clear input on success
    setShowBindConfirmation(false);
    setInviteCodeInput('');
    setValidationError(null);
  };

  const cancelBindCode = () => {
    setShowBindConfirmation(false);
  };

  // Loading or not connected state
  if (!isConnected || !isAuthenticated || isLoadingInfo || isLoadingDashboard) {
    return (
      <div className="min-h-screen" style={{ position: 'relative' }}>
        <TopNav
          onHistoryClick={() => setHistoryOpen(true)}
          onFaqClick={() => setFaqOpen(true)}
          onPrizeTiersClick={() => setPrizeTiersOpen(true)}
          onSettingsClick={() => setSettingsOpen(true)}
        onNFTGalleryClick={() => navigate('/my-nft')}
        />

        <div className="content-container-with-noise px-4 py-8 flex items-center justify-center">
          <div className="text-center">
            {!isConnected ? (
              <>
                <div className="text-6xl mb-4">👥</div>
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>CONNECT YOUR WALLET</h2>
                <p className="text-gray-300" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Connect your wallet to view your referral rewards
                </p>
              </>
            ) : !isAuthenticated ? (
              <>
                <div className="text-6xl mb-4">🔐</div>
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>AUTHENTICATE</h2>
                <p className="text-gray-300 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sign a message to authenticate and access referral features
                </p>
                <button
                  onClick={authenticate}
                  disabled={isAuthenticating}
                  className="py-3 px-12 font-bold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    fontSize: '24px',
                    borderRadius: '100px',
                    background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                    boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                    color: '#000',
                  }}
                >
                  {isAuthenticating ? 'SIGNING...' : 'SIGN MESSAGE'}
                </button>
              </>
            ) : (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#50E5F8' }} />
                <div className="text-gray-300" style={{ fontFamily: 'Outfit, sans-serif' }}>Loading...</div>
              </>
            )}
          </div>
        </div>

        <BottomNavigation />
        <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
        <FaqDrawer open={faqOpen} onClose={() => setFaqOpen(false)} />
        <PrizeTiersDrawer open={prizeTiersOpen} onClose={() => setPrizeTiersOpen(false)} />
        <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>
    );
  }

  const referralLink = inviteInfo?.invite_code
    ? `${window.location.origin}/?ref=${inviteInfo.invite_code}`
    : '';
  const inviteCode = inviteInfo?.invite_code || 'N/A';
  const boundToCode = dashboard?.invited_by_code || 'None';
  const totalInvitees = dashboard?.total_invitees || 0;

  return (
    <div className="min-h-screen" style={{ position: 'relative' }}>
      <TopNav
        onHistoryClick={() => setHistoryOpen(true)}
        onFaqClick={() => setFaqOpen(true)}
        onPrizeTiersClick={() => setPrizeTiersOpen(true)}
        onSettingsClick={() => setSettingsOpen(true)}
        onNFTGalleryClick={() => navigate('/my-nft')}
      />

      <div className="content-container-with-noise px-4 py-8">
        {/* Header */}
        <h1
          className="text-center mb-8"
          style={{
            fontFamily: 'Hyperspace Race Capsule, cursive',
            fontSize: window.innerWidth < 768 ? '32px' : '48px',
            fontWeight: 900,
            color: '#FFF',
            textShadow: '0 4px 10.4px rgba(0, 0, 0, 0.50)',
          }}
        >
          REFERRAL REWARDS
        </h1>

        {/* Top Section - Invitation Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Left Box - Your Invitation */}
          <div
            className="p-6 rounded-lg"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              border: '2px solid #50E4F9',
            }}
          >
            <div className="space-y-4">
              <div>
                <label
                  className="block mb-2"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '14px',
                    color: '#FFF',
                  }}
                >
                  Your invitation link:
                </label>
                <div className="flex items-center gap-2">
                  <a
                    href={referralLink}
                    className="flex-1 truncate"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontSize: '14px',
                      color: '#50E5F8',
                      textDecoration: 'underline',
                    }}
                  >
                    {referralLink}
                  </a>
                  <button
                    onClick={() => copyToClipboard(referralLink)}
                    className="p-2 rounded transition-colors hover:bg-white/10"
                    style={{
                      backgroundColor: 'transparent',
                      color: '#50E5F8',
                    }}
                  >
                    <Copy size={20} />
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <span
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontSize: '14px',
                      color: '#FFF',
                    }}
                  >
                    Your invitation code: {inviteCode}
                  </span>
                  <button
                    onClick={() => copyToClipboard(inviteCode)}
                    className="p-1 rounded transition-colors hover:bg-white/10"
                    style={{
                      backgroundColor: 'transparent',
                      color: '#50E5F8',
                    }}
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Box - Bound Info */}
          <div
            className="p-6 rounded-lg"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              border: boundToCode !== 'None' ? '2px solid #22C55E' : '2px solid #50E4F9',
            }}
          >
            <div className="space-y-4">
              {boundToCode !== 'None' ? (
                <>
                  {/* Bound State */}
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle2 size={20} style={{ color: '#22C55E' }} />
                      <div
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontSize: '14px',
                          color: '#22C55E',
                          fontWeight: 600,
                        }}
                      >
                        REFERRAL ACTIVE
                      </div>
                    </div>
                    <div
                      className="mb-2"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      Bound to Referral Code:
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div
                        style={{
                          fontFamily: 'Hyperspace Race Capsule, cursive',
                          fontSize: '28px',
                          color: '#22C55E',
                          letterSpacing: '2px',
                        }}
                      >
                        {boundToCode}
                      </div>
                      <button
                        onClick={() => copyToClipboard(boundToCode)}
                        className="p-2 rounded transition-colors hover:bg-white/10"
                        style={{
                          backgroundColor: 'transparent',
                          color: '#FEBB01',
                        }}
                        title="Copy code"
                      >
                        <Copy size={18} />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Not Bound State */}
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <AlertCircle size={20} style={{ color: '#FEBB01' }} />
                      <div
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontSize: '14px',
                          color: '#FEBB01',
                          fontWeight: 600,
                        }}
                      >
                        NO REFERRAL BOUND
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: 1.5,
                      }}
                    >
                      Bind a referral code below to start using Junk Fun!
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* VIP Referral Campaign Progress - Only shows if user is eligible */}
        <div className="mb-6">
          <ReferralCampaignProgress />
        </div>

        {/* Bind Invite Code Section - Show only if not bound yet */}
        {boundToCode === 'None' && (
          <div
            className="p-6 rounded-lg mb-6"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              border: '2px solid #FEBB01',
            }}
          >
            <div className="text-center mb-4">
              <div
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontSize: '24px',
                  color: '#FEBB01',
                }}
              >
                BIND INVITE CODE
              </div>
              <div
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginTop: '8px',
                }}
              >
                Enter a 5-character invite code to get bonus rewards
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={inviteCodeInput}
                    onChange={(e) => handleInviteCodeChange(e.target.value)}
                    maxLength={5}
                    placeholder="XXXXX"
                    className="w-full p-3 rounded-lg text-center uppercase"
                    style={{
                      backgroundColor: '#343434',
                      border: validationError ? '2px solid #EF4444' : '2px solid #FEBB01',
                      color: '#FFF',
                      fontFamily: 'Hyperspace Race Capsule, cursive',
                      fontSize: window.innerWidth < 768 ? '18px' : '20px',
                      letterSpacing: '4px',
                    }}
                    disabled={isBindingCode}
                  />
                  {validationError && (
                    <div
                      className="mt-1 text-sm"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#EF4444',
                      }}
                    >
                      {validationError}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleBindCodeClick}
                  disabled={isBindingCode || inviteCodeInput.length !== 5}
                  className="py-3 px-8 sm:px-12 font-bold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    fontSize: window.innerWidth < 768 ? '18px' : '20px',
                    borderRadius: '100px',
                    background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                    boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                    color: '#000',
                  }}
                >
                  {isBindingCode ? 'BINDING...' : 'BIND'}
                </button>
              </div>

              <div
                className="text-xs text-center"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  color: 'rgba(255, 255, 255, 0.5)',
                }}
              >
                Allowed characters: A-Z, 2-9 (excluding L, O, 1, 0)
              </div>
            </div>
          </div>
        )}

        {/* Middle Section - Referral Statistics */}
        <div
          className="p-6 rounded-lg mb-6"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            border: '2px solid #FEBB01',
          }}
        >
          <div className="text-center">
            <div
              className="mb-2"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: window.innerWidth < 768 ? '24px' : '32px',
                fontWeight: 700,
                color: '#FEBB01',
              }}
            >
              REFERRAL STATISTICS
            </div>
            <div
              className="mb-4"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              Track your referral progress and rewards
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Total Invitees */}
              <div className="text-center">
                <div
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '8px',
                  }}
                >
                  Total Invitees
                </div>
                <div
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '36px',
                    fontWeight: 700,
                    color: '#FEBB01',
                  }}
                >
                  {totalInvitees}
                </div>
              </div>

              {/* Total Invitee Chests */}
              <div className="text-center">
                <div
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '8px',
                  }}
                >
                  Invitee Chests Opened
                </div>
                <div
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '36px',
                    fontWeight: 700,
                    color: '#9548FC',
                  }}
                >
                  {dashboard?.total_invitee_chests || 0}
                </div>
              </div>
            </div>

            <div
              className="mt-4 text-xs"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: 'rgba(255, 255, 255, 0.6)',
              }}
            >
              Referral points are automatically added to your balance when earned
            </div>
          </div>
        </div>

      </div>

      <BottomNavigation />

      {/* Drawers */}
      <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <FaqDrawer open={faqOpen} onClose={() => setFaqOpen(false)} />
      <PrizeTiersDrawer open={prizeTiersOpen} onClose={() => setPrizeTiersOpen(false)} />
      <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Bind Confirmation Dialog */}
      {showBindConfirmation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={cancelBindCode}
        >
          <div
            className="max-w-md w-full p-6 rounded-lg"
            style={{
              backgroundColor: 'rgba(20, 20, 20, 0.95)',
              border: '2px solid #FEBB01',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div
                className="mb-4"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontSize: '28px',
                  color: '#FEBB01',
                }}
              >
                CONFIRM BINDING
              </div>
              <div
                className="mb-4"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.6,
                }}
              >
                You are about to bind your account to referral code:
              </div>
              <div
                className="mb-4 p-3 rounded"
                style={{
                  backgroundColor: 'rgba(254, 187, 1, 0.1)',
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontSize: '32px',
                  color: '#FEBB01',
                  letterSpacing: '4px',
                }}
              >
                {inviteCodeInput.toUpperCase()}
              </div>
              <div
                className="mb-2"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.6,
                }}
              >
                <strong style={{ color: '#FEBB01' }}>⚠️ Warning:</strong> This action is permanent and cannot be undone. You will earn 1% cashback on all chest openings.
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={cancelBindCode}
                disabled={isBindingCode}
                className="flex-1 py-3 px-6 font-bold rounded-lg transition-all hover:opacity-80 disabled:opacity-50"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontSize: '18px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFF',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                CANCEL
              </button>
              <button
                onClick={confirmBindCode}
                disabled={isBindingCode}
                className="flex-1 py-3 px-6 font-bold rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontSize: '18px',
                  background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                  boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                  color: '#000',
                }}
              >
                {isBindingCode ? 'BINDING...' : 'CONFIRM'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referral Error Modal */}
      {bindError && (
        <ReferralErrorModal
          open={!!bindError}
          onClose={clearBindError}
          error={bindError}
        />
      )}
    </div>
  );
}
